import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useUpdateProject } from '@/hooks/use-projects';
import { toast } from '@/components/ui/toaster';
import {
  Folder,
  Briefcase,
  Star,
  Heart,
  Zap,
  Target,
  Rocket,
  Flag,
  BookOpen,
  Code,
  Globe,
  Home,
  ShoppingCart,
  Users,
  Settings,
  Lightbulb,
  Music,
  Camera,
  Mail,
  Calendar,
} from 'lucide-react';

const PROJECT_COLORS = [
  '#3b82f6', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316',
  '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#6366f1',
  '#a855f7', '#d946ef', '#0ea5e9', '#84cc16', '#64748b',
];

const PROJECT_ICONS = [
  { name: 'folder', icon: Folder, label: 'Folder' },
  { name: 'briefcase', icon: Briefcase, label: 'Briefcase' },
  { name: 'star', icon: Star, label: 'Star' },
  { name: 'heart', icon: Heart, label: 'Heart' },
  { name: 'zap', icon: Zap, label: 'Zap' },
  { name: 'target', icon: Target, label: 'Target' },
  { name: 'rocket', icon: Rocket, label: 'Rocket' },
  { name: 'flag', icon: Flag, label: 'Flag' },
  { name: 'book-open', icon: BookOpen, label: 'Book' },
  { name: 'code', icon: Code, label: 'Code' },
  { name: 'globe', icon: Globe, label: 'Globe' },
  { name: 'home', icon: Home, label: 'Home' },
  { name: 'shopping-cart', icon: ShoppingCart, label: 'Shopping' },
  { name: 'users', icon: Users, label: 'Users' },
  { name: 'settings', icon: Settings, label: 'Settings' },
  { name: 'lightbulb', icon: Lightbulb, label: 'Idea' },
  { name: 'music', icon: Music, label: 'Music' },
  { name: 'camera', icon: Camera, label: 'Camera' },
  { name: 'mail', icon: Mail, label: 'Mail' },
  { name: 'calendar', icon: Calendar, label: 'Calendar' },
];

interface ProjectForSettings {
  id: string;
  name: string;
  color: string;
  icon?: string | null;
  description?: string | null;
}

interface ProjectSettingsDialogProps {
  project: ProjectForSettings;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProjectSettingsDialog({
  project,
  open,
  onOpenChange,
}: ProjectSettingsDialogProps) {
  const updateProject = useUpdateProject();
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || '');
  const [color, setColor] = useState(project.color);
  const [icon, setIcon] = useState<string | null>(project.icon || null);

  useEffect(() => {
    if (open) {
      setName(project.name);
      setDescription(project.description || '');
      setColor(project.color);
      setIcon(project.icon || null);
    }
  }, [open, project]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateProject.mutateAsync({
        id: project.id,
        data: {
          name: name.trim(),
          description: description.trim() || undefined,
          color,
          icon,
        },
      });
      onOpenChange(false);
      toast({ title: 'Project updated successfully' });
    } catch {
      toast({
        title: 'Failed to update project',
        variant: 'destructive',
      });
    }
  };

  const getIconComponent = (iconName: string | null) => {
    if (!iconName) return Folder;
    const found = PROJECT_ICONS.find((i) => i.name === iconName);
    return found?.icon || Folder;
  };

  const SelectedIcon = getIconComponent(icon);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Project Settings</DialogTitle>
          <DialogDescription>
            Customize your project's appearance
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSave}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="project-name">Name</Label>
              <Input
                id="project-name"
                placeholder="Project name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-description">Description</Label>
              <Textarea
                id="project-description"
                placeholder="What's this project about?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex gap-2 flex-wrap">
                {PROJECT_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`h-7 w-7 rounded-full transition-all ${
                      color === c ? 'ring-2 ring-offset-2 ring-offset-background ring-primary' : ''
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="h-10 w-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: color }}
                >
                  <SelectedIcon className="h-5 w-5 text-white" />
                </div>
                <span className="text-sm text-muted-foreground">Preview</span>
              </div>
              <div className="grid grid-cols-10 gap-1">
                {PROJECT_ICONS.map((item) => {
                  const IconComponent = item.icon;
                  const isSelected = icon === item.name;
                  return (
                    <button
                      key={item.name}
                      type="button"
                      onClick={() => setIcon(item.name)}
                      title={item.label}
                      className={`h-8 w-8 rounded flex items-center justify-center transition-all ${
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <IconComponent className="h-4 w-4" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateProject.isPending}>
              {updateProject.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export { PROJECT_ICONS };
